import type { ClikArtCategory } from '@/api/types/clikart'

/**
 * Pulls the category list out of a /clikart/categories or /clikart/search response. Accepts the
 * paged `{ categories, page, size, totalCategories, totalPages, hasMore }` object the categories
 * endpoint returns, the `{ result, payload }` envelope, or a bare category array, so the client
 * keeps working whichever shape the endpoint settles on. Returns null for anything else
 * (including an envelope with a falsy `result`), which callers treat as a failed load.
 */
export function extractClikArtCategories(response: unknown): ClikArtCategory[] | null {
  if (Array.isArray(response)) {
    return response as ClikArtCategory[]
  }

  if (response !== null && typeof response === 'object') {
    const { categories, result, payload } = response as {
      categories?: unknown
      result?: unknown
      payload?: unknown
    }

    if (Array.isArray(categories)) {
      return categories as ClikArtCategory[]
    }

    if (result && Array.isArray(payload)) {
      return payload as ClikArtCategory[]
    }
  }

  return null
}

/**
 * Whether the server says another page follows, read from the paged categories object's `hasMore`.
 * `undefined` for a response that doesn't carry the flag (a bare array or `{ result, payload }`),
 * where the caller has to fall back to its own end-of-pages rule.
 */
export function readClikArtCategoriesHasMore(response: unknown): boolean | undefined {
  if (response === null || typeof response !== 'object' || Array.isArray(response)) {
    return undefined
  }

  const { hasMore } = response as { hasMore?: unknown }

  return typeof hasMore === 'boolean' ? hasMore : undefined
}
